import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Calling V2 → Hold Music.
 *
 * Admin uploads MP3/WAV; backend forwards to runtime POST /v1/hold-music
 * and stores the returned moh_id. Customers can attach an entry when
 * binding a DID.
 */
export default function CallingV2HoldMusic() {
  document.title = "Digital Employees — V2 Hold Music";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const fileRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await CallingV2.listHoldMusic();
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load hold music.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { triggerAlert("error", "Oops...", "Pick an MP3 or WAV file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (displayName.trim()) fd.append("display_name", displayName.trim());
      await CallingV2.uploadHoldMusic(fd);
      triggerAlert("success", "Uploaded", "Hold music added.");
      setDisplayName("");
      if (fileRef.current) fileRef.current.value = "";
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.detail || "Upload failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setUploading(false); }
  };

  const toggleStatus = async (row) => {
    try {
      await CallingV2.updateHoldMusic(row.id, { is_active: !row.is_active });
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Could not update."); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete hold music?",
      message: `Delete hold music "${row.display_name}"?`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteHoldMusic(row.id);
      triggerAlert("success", "Deleted", "Hold music removed.");
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Delete failed."); }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Name", sortable: true, selector: (r) => r.display_name, cell: (r) => <span style={{ fontWeight: 600 }}>{r.display_name}</span> },
    {
      name: "Runtime ID", sortable: true, selector: (r) => r.runtime_moh_id,
      cell: (r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.runtime_moh_id || "—"}</span>,
    },
    { name: "Duration", selector: (r) => r.duration_sec, width: "120px", cell: (r) => <span>{r.duration_sec ? `${r.duration_sec}s` : "—"}</span> },
    {
      name: "Preview", width: "200px",
      cell: (r) => r.preview_url
        ? <audio controls src={r.preview_url} style={{ width: 180, height: 32 }} />
        : <span className="text-muted small">—</span>,
    },
    {
      name: "Status", sortable: true, selector: (r) => r.is_active, width: "110px",
      cell: (r) => r.is_active
        ? <span className="badge bg-success">Active</span>
        : <span className="badge bg-danger">Inactive</span>,
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`} onClick={() => toggleStatus(r)}>
            {r.is_active ? "Pause" : "Activate"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "#064c77", borderBottom: "1px solid #dee2e6", minHeight: "52px" } },
    headCells: { style: { color: "#ffffff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "60px", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8f9fa" } } },
    cells: { style: { fontSize: "14px", color: "#495057", borderBottom: "1px solid #f1f3f5" } },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="Hold Music" heading="Calling V2 / Hold Music" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                Upload MP3 or WAV audio that customers can pick when routing an inbound DID.
                Maximum 20MB per file.
              </div>

              <form onSubmit={onUpload} className="row align-items-end mb-3 g-2">
                <div className="col-md-5">
                  <label className="form-label">Audio file</label>
                  <input type="file" ref={fileRef} accept=".mp3,.wav" className="form-control" />
                </div>
                <div className="col-md-5">
                  <label className="form-label">Display name (optional)</label>
                  <input className="form-control" placeholder="e.g. Calm Piano" value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)} />
                </div>
                <div className="col-md-2 text-end">
                  <button type="submit" className="btn btn-primary w-100" disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </form>

              {!loading ? (
                <DataTable columns={columns} data={rows} pagination fixedHeader fixedHeaderScrollHeight="640px" persistTableHead customStyles={customStyles}
                  noDataComponent={<div style={{ padding: 24 }}>No hold music yet. Upload one above.</div>} />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
